const LOGIN_APP_KEY = "im.loginApp";
const ACTIVE_APP_KEY = "im.activeApp";

export const APP_ACCESS_KEYS = Object.freeze(["neo", "mc", "ontime", "league"]);

export function normalizeAppName(input) {
  const value = String(input || "").trim().toLowerCase();
  if (["cashup", "neo", "neo_admin", "cash-up", "admin", "dashboard"].includes(value)) return "neo";
  if (["mc", "morpheus", "morpheus-count", "morphius", "scs"].includes(value)) return "mc";
  if (["ontime", "on_time", "link"].includes(value)) return "ontime";
  if (["league", "matrix-league", "matrix_league"].includes(value)) return "league";
  return value;
}

export function normalizeApps(input) {
  const values = [];

  function addApp(app) {
    const normalized = normalizeAppName(app);
    if (normalized) values.push(normalized);
  }

  function collect(raw) {
    if (!raw) return;

    if (Array.isArray(raw)) {
      raw.forEach(addApp);
      return;
    }

    if (typeof raw === "object") {
      Object.entries(raw).forEach(([key, enabled]) => {
        if (enabled === true || enabled === "true" || enabled === 1) addApp(key);
      });
    }
  }

  // Keep support for every access shape we have used during the Neo/MC/Link rewrite.
  // This avoids breaking older orgs while allowing newly created orgs to work too.
  if (input && typeof input === "object" && !Array.isArray(input)) {
    collect(input.apps);        // legacy: ["morpheus"] or { morpheus: true }
    collect(input.enabledApps); // org-level: ["morpheus"]
    collect(input.appAccess);   // member-level: { mc: true }
    collect(input.appsMap);     // migration bridge: { morpheus: true }
    collect(input.access);
    collect(input.permissions);

    // Product-specific access records used by Super Admin/member documents.
    // League viewers commonly receive access as { leagueAccess: { enabled: true, ... } }
    // rather than a flat apps/appAccess flag, so preserve that access shape too.
    if (input.leagueAccess?.enabled === true || input.leagueAccess === true) addApp("league");
    if (input.neoAccess?.enabled === true || input.neoAccess === true) addApp("neo");
    if (input.morpheusAccess?.enabled === true || input.morpheusAccess === true) addApp("mc");
    if (input.onTimeAccess?.enabled === true || input.onTimeAccess === true || input.linkAccess?.enabled === true || input.linkAccess === true) addApp("ontime");

    addApp(input.defaultApp);
  } else {
    collect(input);
  }

  return [...new Set(values.filter(Boolean))];
}

function hasAlias(claims = {}, aliases = []) {
  const isSuper = claims?.superAdmin === true || claims?.superadmin === true;
  if (isSuper) return true;

  const apps = normalizeApps(claims);
  return apps.some((app) => aliases.includes(app));
}

export function hasNeoAccess(claims = {}) {
  return hasAlias(claims, ["neo"]);
}

export function hasMorpheusAccess(claims = {}) {
  return hasAlias(claims, ["mc"]);
}

export function hasOnTimeAccess(claims = {}) {
  return hasAlias(claims, ["ontime"]);
}

export function hasLeagueAccess(claims = {}) {
  return hasAlias(claims, ["league"]);
}

export function setActiveApp(app) {
  const value = normalizeAppName(app);
  if (!value || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(ACTIVE_APP_KEY, value);
    window.localStorage.setItem(LOGIN_APP_KEY, value);
  } catch (_) {}
}

export function getActiveApp() {
  if (typeof window === "undefined") return "";
  try {
    return normalizeAppName(
      window.sessionStorage.getItem(ACTIVE_APP_KEY) ||
        window.localStorage.getItem(LOGIN_APP_KEY) ||
        ""
    );
  } catch (_) {
    return "";
  }
}

export function clearActiveApp() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(ACTIVE_APP_KEY);
    window.localStorage.removeItem(LOGIN_APP_KEY);
  } catch (_) {}
}

export function clearMatrixSessionState() {
  if (typeof window === "undefined") return;
  try {
    clearActiveApp();
    [
      "mc-force-access-layer",
      "mc-access-layer-unlocked",
      "mc-current-profile",
      "mc-active-profile-current",
      "mc-user-context",
      "im.ontime.session",
      "matrixos.link.accessProfile.v1",
    ].forEach((key) => {
      window.sessionStorage.removeItem(key);
      window.localStorage.removeItem(key);
    });
  } catch (_) {}
}
